import type { PlaylistItem, Source } from '@jwp/ott-common/types/playlist';
import type { Config } from '@jwp/ott-common/types/config';
import { MANIFEST_TYPE, MEDIA_CONTENT_TYPE } from '@jwp/ott-common/src/constants';

import { isContentType } from './common';

export const getSources = ({
  item,
  baseUrl,
  config,
  userId: _userId,
  passport,
}: {
  item: PlaylistItem;
  baseUrl: string;
  config: Config;
  userId: string | undefined;
  passport: string | null;
}) => {
  const { sources, mediaid } = item;
  const { adConfig, siteId, adDeliveryMethod } = config;

  const hasServerAds = !!adConfig && adDeliveryMethod === 'ssai';

  return sources.map((source: Source) => {
    const url = new URL(source.file);

    const isDRM = (url.searchParams.has('exp') && url.searchParams.has('sig')) || source.drm;

    // Use SSAI URL for configs with server side ads, DRM is not supported yet
    if (hasServerAds && !isDRM) {
      url.href = `${baseUrl}/v2/sites/${siteId}/media/${mediaid}/ssai.${MANIFEST_TYPE.dash === source.type ? 'mpd' : 'm3u8'}`;
      url.searchParams.set('ad_config_id', adConfig);
    }

    // @CUSTOMER vd-ott-iq User id should not be added
    // Attach user_id for VOD and BCL SaaS Live Streams
    // if (userId) {
    // url.searchParams.set('user_id', userId);
    // }

    // Attach the passport in all the drm sources as it's needed for the licence request.
    // Passport is only available if Access Bridge is in use.
    if (passport) {
      attachPassportToSourceWithDRM(source, passport);
    }

    source.file = url.toString();

    return source;
  });
};

function attachPassportToSourceWithDRM(source: Source, passport: string): Source {
  function updateUrl(urlString: string, passport: string): string {
    const url = new URL(urlString);
    if (!url.searchParams.has('token')) {
      url.searchParams.set('passport', passport);
    }
    return url.toString();
  }

  if (source?.drm) {
    if (source.drm?.playready?.url) {
      source.drm.playready.url = updateUrl(source.drm.playready.url, passport);
    }
    if (source.drm?.widevine?.url) {
      source.drm.widevine.url = updateUrl(source.drm.widevine.url, passport);
    }
    if (source.drm?.fairplay?.processSpcUrl) {
      source.drm.fairplay.processSpcUrl = updateUrl(source.drm.fairplay.processSpcUrl, passport);
    }
  }

  return source;
}

export const hasWatchableContentType = (item: PlaylistItem) => {
  if (isContentType(item, MEDIA_CONTENT_TYPE.hub)) return false;
  if (isContentType(item, MEDIA_CONTENT_TYPE.liveChannel)) return false;
  if (isContentType(item, MEDIA_CONTENT_TYPE.page)) return false;

  return true;
};

export const hasVideo = (item: PlaylistItem) => {
  if (!hasWatchableContentType(item)) return false;

  return !!item.sources?.some((source) => {
    const file = source?.file?.toLowerCase() || '';

    return file.endsWith('.m3u8') || file.endsWith('.mpd') || file.endsWith('.mp4') || file.endsWith('.mov') || file.endsWith('.webm') || file.endsWith('.mkv');
  });
};
